import { Box, alpha, useTheme } from '@mui/material';

interface Props {
	edge: 'top' | 'bottom';
	/** Whether there is clipped content on this edge (see `useScrollShade`). */
	visible: boolean;
	/** Height of the fade/shadow band. */
	size?: string;
	/** Surface colour the content fades into. Defaults to the primary background. */
	surface?: string;
}

/**
 * A scroll shadow drawn *on top* of scrollable content (`pointer-events: none`),
 * so a filled element at the edge can't hide it. Pair with `useScrollShade`,
 * which decides when it should be `visible`.
 *
 * Render it as the first (`top`) / last (`bottom`) child of the scroll
 * container; it pins to the viewport edge via `position: sticky` and overlaps
 * the content with a negative margin, so it never affects layout.
 */
const ScrollShade = ({ edge, visible, size = '24px', surface }: Props) => {
	const { palette, transitions } = useTheme();
	const top = edge === 'top';
	const bg = surface ?? palette.bgPrimary.main;
	const shadow = palette.bgPrimarySolid.main;
	const intensity = 0.5;

	return (
		<Box
			aria-hidden
			sx={{
				position: 'sticky',
				[edge]: 0,
				height: size,
				flexShrink: 0,
				zIndex: 2,
				pointerEvents: 'none',
				[top ? 'mb' : 'mt']: `-${size}`,
				background: `
					linear-gradient(${top ? bg : alpha(bg, 0)}, ${top ? alpha(bg, 0) : bg}),
					radial-gradient(farthest-side at 50% ${top ? '0' : '100%'}, ${alpha(shadow, intensity)}, transparent)`,
				backgroundRepeat: 'no-repeat',
				backgroundSize: '100% 100%, 100% 8px',
				backgroundPosition: top ? '0 0, 0 0' : '0 100%, 0 100%',
				opacity: visible ? 1 : 0,
				transition: transitions.create('opacity', {
					duration: transitions.duration.standard,
				}),
			}}
		/>
	);
};

export default ScrollShade;
